import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Tab from "components/DesignSystem/Tab/Tab";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { useEffect, useMemo, useState } from "react";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import {
  useDeleteCrmLayoutNodeMutation,
  useGetEntityCrmFieldsQuery,
} from "store/apis/crmFields";
import { CrmField, CrmSection, CrmTab } from "types/Models/crmFields";
import { CRMFieldAuditLogModal } from "./CRMFieldAuditLogModal";
import { CRMFieldFormModal } from "./CRMFieldFormModal";
import { CRMNodeTitleModal } from "./CRMNodeTitleModal";
import { CRMTabPanel } from "./CRMTabPanel";

type TitleModalState = {
  nodeKind: "tab" | "section";
  nodeId?: string | null;
  parentId?: string | null;
  initialTitle?: string;
};

type FieldModalState = {
  section?: CrmSection | null;
  field?: CrmField | null;
};

/** The CRM tabs on the company account overview, for one company entity. */
export const CompanyCRMFields = ({ entityId }: { entityId: string }) => {
  const { isAdmin } = useRoleBasedView();
  const { alertToast, successToast } = useToast();
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [titleModal, setTitleModal] = useState<TitleModalState>({
    nodeKind: "section",
  });
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [fieldModal, setFieldModal] = useState<FieldModalState>({});
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
  const [auditLogField, setAuditLogField] = useState<CrmField | null>(null);
  const [deleteNode] = useDeleteCrmLayoutNodeMutation();

  const { currentData, isFetching, isError } = useGetEntityCrmFieldsQuery(
    { entityId },
    { skip: !entityId, refetchOnMountOrArgChange: true }
  );

  const isLoading = isFetching && !currentData;
  const isSuccess = Boolean(currentData);
  const tabs = useMemo(() => currentData?.tabs || [], [currentData?.tabs]);

  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((tab) => tab.uuid === activeTabId)) {
      setActiveTabId(tabs[0].uuid);
    }
  }, [tabs, activeTabId]);

  const onRemoveField = async (section: CrmSection, field: CrmField) => {
    try {
      await deleteNode({
        field: { node_id: field.uuid, parent_id: section.uuid },
      }).unwrap();
      successToast({ message: `${field.label} removed from ${section.title}` });
    } catch (error: any) {
      alertToast({
        message: error?.data?.message || "Could not remove the field",
      });
    }
  };

  const onDeleteSection = async (section: CrmSection) => {
    try {
      await deleteNode({ section: { node_id: section.uuid } }).unwrap();
      successToast({ message: `${section.title} deleted` });
    } catch (error: any) {
      alertToast({
        message: error?.data?.message || "Could not delete the section",
      });
    }
  };

  return (
    <Async.Root
      isLoading={isLoading}
      isError={isError}
      isSuccess={isSuccess}
      isEmpty={tabs.length === 0}
    >
      <Async.Empty>
        <div className="t-flex t-flex-col t-gap-4 t-items-center">
          <EmptyScreen text="No CRM tabs configured yet" />
          {isAdmin && (
            <Button
              size="small"
              onClick={() => {
                setTitleModal({ nodeKind: "tab" });
                setIsTitleModalOpen(true);
              }}
            >
              Add tab
            </Button>
          )}
        </div>
      </Async.Empty>
      <Async.Success>
        <Tab.Root
          value={activeTabId || undefined}
          onValueChange={setActiveTabId}
        >
          <div className="t-flex t-items-end t-justify-between t-gap-4">
            <Tab.List>
              {tabs.map((tab) => (
                <Tab.Trigger key={tab.uuid} value={tab.uuid}>
                  {tab.title}
                </Tab.Trigger>
              ))}
            </Tab.List>
            {isAdmin && (
              <div className="t-flex t-gap-2 t-pb-2 t-shrink-0">
                <Button
                  size="small"
                  customType="ghost"
                  onClick={() => {
                    setTitleModal({ nodeKind: "tab" });
                    setIsTitleModalOpen(true);
                  }}
                >
                  Add tab
                </Button>
              </div>
            )}
          </div>

          {tabs.map((tab: CrmTab) => (
            <Tab.Content key={tab.uuid} value={tab.uuid}>
              <CRMTabPanel
                key={entityId}
                tab={tab}
                entityId={entityId}
                isAdmin={Boolean(isAdmin)}
                onAddSection={(parentTab) => {
                  setTitleModal({
                    nodeKind: "section",
                    parentId: parentTab.uuid,
                  });
                  setIsTitleModalOpen(true);
                }}
                onEditSection={(section) => {
                  setTitleModal({
                    nodeKind: "section",
                    nodeId: section.uuid,
                    initialTitle: section.title,
                  });
                  setIsTitleModalOpen(true);
                }}
                onDeleteSection={onDeleteSection}
                onAddField={(section) => {
                  setFieldModal({ section });
                  setIsFieldModalOpen(true);
                }}
                onEditField={(field) => {
                  setFieldModal({ field });
                  setIsFieldModalOpen(true);
                }}
                onRemoveField={onRemoveField}
                onViewAuditLog={setAuditLogField}
              />
            </Tab.Content>
          ))}
        </Tab.Root>

        <CRMNodeTitleModal
          isOpen={isTitleModalOpen}
          close={() => setIsTitleModalOpen(false)}
          entityId={entityId}
          nodeKind={titleModal.nodeKind}
          nodeId={titleModal.nodeId}
          parentId={titleModal.parentId}
          initialTitle={titleModal.initialTitle}
        />

        <CRMFieldFormModal
          isOpen={isFieldModalOpen}
          close={() => setIsFieldModalOpen(false)}
          section={fieldModal.section}
          field={fieldModal.field}
        />

        {auditLogField && (
          <CRMFieldAuditLogModal
            isOpen
            close={() => setAuditLogField(null)}
            entityId={entityId}
            field={auditLogField}
          />
        )}
      </Async.Success>
    </Async.Root>
  );
};
